import React, { useState, useCallback, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { selectLevel, selectArea, resetResidences } from './index';
import { data } from './dataResidences';

function ConnectViewResidencesMenu({ selectedLevel, currRotation, isLevelGroupActive, selectLevel, selectArea, resetResidences }) {
  const [menuOpen, setMenuOpen] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // After first render, enable transitions for subsequent open/close
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);
  const [highlightedSub, setHighlightedSub] = useState(null); // click-activated
  const [hoveredSub, setHoveredSub] = useState(null);         // hover-activated

  // Clear highlight when floorplate is closed
  useEffect(() => {
    if (!isLevelGroupActive) {
      setHighlightedSub(null);
    }
  }, [isLevelGroupActive]);

  // Clear local state when Redux selectedLevel is reset (e.g. on route entry)
  useEffect(() => {
    if (!selectedLevel) {
      setHighlightedSub(null);
      setHoveredSub(null);
    }
  }, [selectedLevel]);

  const menuRef = useRef(null);

  const levelsData = Object.keys(data.levelGroups)
    .filter(key => key !== 'Type')
    .map(key => ({
      key,
      title: key,
      subLevels: data.levelGroups[key],
    }));

  // ── Sub-item click handler ───────────────────────────────────────────────
  // Items WITH rotation overlays: first click = highlight + show overlay,
  //                               second click = enter floorplate
  // Items WITHOUT overlays (Basement): single click = enter floorplate directly

  const handleSubClick = useCallback((subKey, target, e) => {
    e.stopPropagation();
    const hasOverlay = !!data.rotationOverlays[currRotation]?.[subKey];

    if (!hasOverlay) {
      selectLevel(target);
      setHighlightedSub(null);
      selectArea(null);
      return;
    }

    if (highlightedSub === subKey) {
      // Second click — enter floorplate
      selectLevel(target);
      setHighlightedSub(null);
      selectArea(null);
    } else {
      // First click — highlight and show area overlay
      setHighlightedSub(subKey);
      selectArea(subKey);
    }
  }, [currRotation, highlightedSub, selectLevel, selectArea]);

  // Hover still shows overlay on mouse devices (clears on leave)
  const handleSubHover = useCallback((subKey) => {
    setHoveredSub(subKey);
    if (!highlightedSub && data.rotationOverlays[currRotation]?.[subKey]) selectArea(subKey);
  }, [currRotation, highlightedSub, selectArea]);

  const handleSubLeave = useCallback((subKey) => {
    setHoveredSub(null);
    if (highlightedSub !== subKey) selectArea(highlightedSub || null);
  }, [highlightedSub, selectArea]);

  return (
    <div className="residences--menu">
      <button
        className={['residences--menu__toggle', menuOpen ? 'open' : ''].filter(Boolean).join(' ')}
        onClick={(e) => { e.stopPropagation(); setMenuOpen(prev => !prev); }}
        style={{ minWidth: 48, minHeight: 48 }}
      >
        Explore
      </button>

      <ul className={`residences--menu__list ${menuOpen ? 'open' : ''} ${!mounted ? 'no-transition' : ''}`}>
          {levelsData.map(level => (
          <li key={level.key}>
            {level.subLevels && (
              <ul className={`residences--menu__sublist ${menuOpen ? 'open' : ''} ${!mounted ? 'no-transition' : ''}`}>
                {level.subLevels.map((sub) => {
                  const subKey = sub.level || sub.target;
                  const isActive = highlightedSub === subKey || hoveredSub === subKey;

                  return (
                    <li key={sub.target}>
                      <button
                        className={[
                          selectedLevel === sub.target ? 'active' : '',
                          isActive ? 'overlay-active' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(e) => handleSubClick(subKey, sub.target, e)}
                        onMouseEnter={() => handleSubHover(subKey)}
                        onMouseLeave={() => handleSubLeave(subKey)}
                        style={{ minWidth: 48, minHeight: 48, padding: '8px 0px' }}
                      >
                        <span className="residences--menu__list-span">
                          {sub.level || sub.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const mapStateToProps = (state) => ({
  selectedLevel: state.residences.selectedLevel,
  currRotation: state.residences.currRotation,
  isLevelGroupActive: state.residences.isLevelGroupActive,
});

const mapDispatchToProps = { selectLevel, selectArea, resetResidences };

export default connect(mapStateToProps, mapDispatchToProps)(ConnectViewResidencesMenu);
